import { api, API_BASE, getToken } from './api.ts';
import { splitIntoChunks } from './extract.ts';
import type { AufbereitungsErgebnis } from './aufbereitung/index.ts';
import {
  decrypt,
  decryptJson,
  decryptVectors,
  encrypt,
  encryptJson,
  encryptVectors,
  fromBase64,
  generateDek,
  toBase64,
  utf8,
} from './crypto/primitives.ts';
import { importPublicKey, unwrapDek, wrapDekFor, type KeyAlgorithm } from './crypto/keypair.ts';
import { currentOrganisation, requireVault } from './crypto/vault.ts';
import { safeSchluessel } from './safes.ts';
import { dokumentInOrganisationVerschieben, kreisSchluessel } from './schluesselkreise.ts';

/**
 * Upload- und Lese-Ablauf (Konzept 6.1 / 6.5).
 *
 * Der Klartext existiert ausschließlich in dieser Datei und nur so lange,
 * wie die Verschlüsselung dauert. Alles, was an api.* übergeben wird, ist
 * bereits verschlüsselt.
 */

export interface DocumentMeta {
  id: string;
  folder_id: string | null;
  category_label: string | null;
  content_type_hint: string | null;
  size_bytes: number;
  uploaded_via: 'customer' | 'buddy' | 'whatsapp_intake' | 'dienst';
  ai_processed: boolean;
  created_at: string;
  filename_encrypted: string;
  wrapped_dek: string;
  /**
   * Woher der Umschlag stammt (Migration 007/010, Schlüsselkreise) — sagt,
   * mit welchem bereits entsperrten Schlüssel `wrapped_dek` zu öffnen ist.
   * `'customer'` (Vorgabe, auch wenn das Feld fehlt) ist der klassische Weg
   * über den eigenen Kontoschlüssel; `'organisation'`/`'kreis'` kommen erst
   * mit dem Schlüssel-Upgrade dazu. Buddy-Ansicht liefert das Feld nicht —
   * dort bleibt es unverändert beim Buddy-eigenen Schlüssel.
   */
  wrap_ziel_art?: 'customer' | 'organisation' | 'kreis' | 'person';
  wrap_ziel_id?: string | null;
  /**
   * Zu welchem Safe das Dokument gehört (Familien-Safes, 16.10.2026) – NULL nur bei
   * Altbestand eines Kontos ohne eigene Organisation; die Oberfläche zählt das zu
   * „Mein Safe".
   */
  safe_kunde_id?: string | null;
  has_text: boolean;
  has_fields: boolean;
  has_embedding: boolean;
  embedding_model: string | null;
  customer_id?: string;
  /** Nur in der Buddy-Ansicht: Name des Kunden, der diese Freigabe erteilt hat. */
  kunde_name?: string;
  expires_at?: string;
}

/** Entschlüsselte Sicht auf ein Dokument – nur im Arbeitsspeicher. */
export interface OpenedDocument {
  id: string;
  filename: string;
  dek: CryptoKey;
  meta: DocumentMeta;
}

export interface StructuredFields {
  amount: number | null;
  currency: string | null;
  date: string | null;
  sender: string | null;
}

/**
 * Obergrenze der eingebetteten Abschnitte je Dokument. Deckelt Wartezeit und
 * Speicher bei sehr langen Verträgen; der Rest bleibt über die Stichwortsuche
 * auffindbar.
 */
const MAX_CHUNKS = 40;

/**
 * Baut einen einheitlichen Dateinamen aus dem, was die KI erkannt hat.
 *
 * Schema: `JJJJ-MM-TT_Kategorie_Absender_Betrag.endung`
 * Beispiel: `2026-06-15_Rechnung_Google-Cloud-EMEA_249-90EUR.pdf`
 *
 * Warum das Datum vorn steht: So sortiert jede Dateiliste – auch außerhalb
 * dieses Systems, etwa nach einem Export – von selbst chronologisch.
 *
 * Fehlt ein Bestandteil, entfällt er ersatzlos; es bleibt immer ein
 * brauchbarer Name übrig. Der ursprüngliche Name geht nicht verloren, wenn
 * gar nichts erkannt wurde – dann wird er unverändert übernommen.
 */
export function buildFilename(
  originalName: string,
  category: string | null,
  fields: StructuredFields | null,
  fallbackDate: Date = new Date(),
): string {
  const endung = originalName.includes('.')
    ? originalName.slice(originalName.lastIndexOf('.')).toLowerCase()
    : '';

  // Umlaute ausschreiben statt zu entfernen: "Vertraege" ist lesbar,
  // "Vertrge" nicht.
  const saubern = (wert: string): string =>
    wert
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
      .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);

  const teile: string[] = [];

  const datum = fields?.date && /^\d{4}-\d{2}-\d{2}$/.test(fields.date)
    ? fields.date
    : fallbackDate.toISOString().slice(0, 10);
  teile.push(datum);

  if (category) teile.push(saubern(category));
  if (fields?.sender) teile.push(saubern(fields.sender));

  if (typeof fields?.amount === 'number' && Number.isFinite(fields.amount)) {
    // Komma im Dateinamen vermeiden – manche Systeme stolpern darüber.
    const betrag = fields.amount.toFixed(2).replace('.', '-');
    teile.push(`${betrag}${saubern(fields.currency ?? 'EUR')}`);
  }

  // Nur Datum erkannt und sonst nichts? Dann sagt der neue Name weniger aus
  // als der alte – in dem Fall den ursprünglichen behalten.
  if (teile.length === 1) return originalName;

  return teile.join('_') + endung;
}

export interface UploadOptions {
  /**
   * Fertig aufbereitetes Dokument (siehe `aufbereitung/`).
   *
   * Bewusst nicht die rohe Datei: Zuschnitt, Entzerrung und Texterkennung
   * brauchen im Zweifel eine Rückfrage beim Nutzer. Diese Funktion soll
   * dagegen ohne Oberfläche auskommen – sie verschlüsselt und lädt hoch,
   * mehr nicht.
   */
  aufbereitet: AufbereitungsErgebnis;
  folderId?: string | null;
  /** Opt-in für die KI-Aufbereitung (Konzept 3.8). */
  useAi: boolean;
  /** Einheitlichen Dateinamen aus dem erkannten Inhalt bilden. */
  renameFile?: boolean;
  /** Nur bei Blindablage durch den Buddy gesetzt. */
  forCustomer?: { customerId: string; publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm };
  /**
   * In welchen Safe (Familien-Safes, 16.10.2026): Der Dokumentschlüssel wird für den
   * Safe-Schlüssel verpackt (alle Mitglieder) und zusätzlich für jeden Gast, der das
   * Dokument sehen soll – bei „alles" immer, bei „Fächer" nur, wenn der Zielordner in
   * einem seiner Fächer liegt (`faecher` enthält bereits alle Unterordner).
   */
  safe?: {
    kundeId: string;
    publicKeyRaw: Uint8Array;
    algorithm: KeyAlgorithm;
    gaeste: Array<{ personId: string; publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm; umfang: 'alles' | 'faecher'; faecher: string[] }>;
    /** Fächer des Safes: Landet das Dokument in einem, wird es für dessen Kreis verpackt statt für den Safe. */
    fachFuer?: (folderId: string | null) => { id: string; publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm } | null;
  };
  onProgress?: (step: string) => void;
}

export async function uploadDocument(options: UploadOptions): Promise<{ id: string }> {
  const { aufbereitet, useAi, onProgress } = options;
  const report = (step: string) => onProgress?.(step);

  // Empfänger bestimmen: eigener Public Key (Self-Upload) oder der des
  // Kunden (Blindablage – funktioniert ohne dessen Beteiligung, Konzept 3.7).
  const recipient = options.forCustomer
    ? { publicKeyRaw: options.forCustomer.publicKeyRaw, algorithm: options.forCustomer.algorithm }
    : { publicKeyRaw: requireVault().publicKeyRaw, algorithm: requireVault().algorithm };

  const plaintext = aufbereitet.bytes;
  // Der Text stammt aus der Aufbereitung – bei einem Scan aus der
  // Texterkennung, sonst aus der Textebene des PDF (Konzept 3.10). Er hat
  // denselben Klartext-Moment genutzt, der beim Verschlüsseln ohnehin besteht.
  const extractedText = aufbereitet.text;

  let categoryLabel: string | null = null;
  let structuredFields: StructuredFields | null = null;
  let embeddings: number[][] | null = null;
  let embeddingModel: string | null = null;
  let folderId = options.folderId ?? null;

  if (useAi && extractedText.trim()) {
    report('Dokument wird einsortiert');
    try {
      // Erst den Bestand holen, dann klassifizieren: Das Modell soll einen
      // vorhandenen Ordner wiederverwenden können, statt einen neuen zu
      // erfinden. Schlägt das Laden fehl, wird trotzdem sortiert — nur eben
      // ohne diesen Hinweis.
      const bestand = await ordnerNamen(options.forCustomer?.customerId, options.safe?.kundeId).catch(() => []);

      const classification = await api.post<{
        category: string;
        folderSuggestion: string;
        fields: StructuredFields;
      }>('/api/ai/classify', {
        text: extractedText,
        filename: aufbereitet.dateiname,
        existingFolders: bestand,
      });

      categoryLabel = classification.category;
      structuredFields = classification.fields;

      if (!folderId && classification.folderSuggestion) {
        folderId = await ensureFolder(classification.folderSuggestion, options.forCustomer?.customerId, options.safe?.kundeId);
      }

      // In Abschnitte zerlegen: Das Modell verarbeitet nur 512 Tokens am
      // Stück. Ein längerer Vertrag bekäme sonst gar keinen Vektor.
      const abschnitte = splitIntoChunks(extractedText).slice(0, MAX_CHUNKS);
      report(`Wird durchsuchbar gemacht (${abschnitte.length} Abschnitte)`);

      const vektoren: number[][] = [];
      for (const [i, abschnitt] of abschnitte.entries()) {
        const embedded = await api.post<{ embedding: number[]; model: string }>(
          '/api/ai/embed',
          { input: abschnitt, inputType: 'document' },
        );
        vektoren.push(embedded.embedding);
        // Festhalten, womit die Vektoren erzeugt wurden – nach einem
        // Modellwechsel ist das die einzige Möglichkeit, veraltete zu erkennen.
        embeddingModel = embedded.model;
        if (abschnitte.length > 3) {
          report(`Wird durchsuchbar gemacht (${i + 1} von ${abschnitte.length})`);
        }
      }
      embeddings = vektoren;
    } catch {
      // Ohne Sortierung ablegen ist immer noch besser als gar nicht ablegen.
      // Das Dokument landet in "Nicht sortiert" und bleibt über die
      // Stichwortsuche auffindbar (Konzept 3.8, letzter Absatz).
      report('Automatische Sortierung nicht möglich – wird unsortiert abgelegt');
    }
  }

  // Einheitlicher Dateiname aus dem, was erkannt wurde. Rein clientseitig –
  // der Name ist verschlüsselt, der Server sieht ihn ohnehin nie.
  const anzeigeName =
    options.renameFile && categoryLabel
      ? buildFilename(aufbereitet.dateiname, categoryLabel, structuredFields)
      : aufbereitet.dateiname;
  if (anzeigeName !== aufbereitet.dateiname) report(`Wird abgelegt als „${anzeigeName}"`);

  report('Wird verschlüsselt');
  const dek = await generateDek();
  const encryptedDocument = await encrypt(dek, plaintext);
  const wrappedDek = await wrapDekFor(dek, recipient.publicKeyRaw, recipient.algorithm);
  const encryptedFilename = await encrypt(dek, utf8.encode(anzeigeName));

  // Safe und Gäste (Familien-Safes): Umschläge für den Safe-Schlüssel und für jeden
  // Gast, der dieses Dokument sehen soll.
  let sichtbarkeit:
    | { art: 'organisation'; kundeId: string; wrappedDek: string }
    | { art: 'kreis'; kreisId: string; wrappedDek: string }
    | undefined;
  const weitereWraps: Array<{ personId: string; wrappedDek: string }> = [];
  if (options.safe && !options.forCustomer) {
    const fach = options.safe.fachFuer?.(folderId) ?? null;
    sichtbarkeit = fach
      ? { art: 'kreis', kreisId: fach.id, wrappedDek: toBase64(await wrapDekFor(dek, fach.publicKeyRaw, fach.algorithm)) }
      : { art: 'organisation', kundeId: options.safe.kundeId, wrappedDek: toBase64(await wrapDekFor(dek, options.safe.publicKeyRaw, options.safe.algorithm)) };
    for (const gast of fach ? [] : options.safe.gaeste) {
      const sieht = gast.umfang === 'alles' || (folderId !== null && gast.faecher.includes(folderId));
      if (!sieht) continue;
      weitereWraps.push({ personId: gast.personId, wrappedDek: toBase64(await wrapDekFor(dek, gast.publicKeyRaw, gast.algorithm)) });
    }
  }

  const form = new FormData();
  form.append(
    'meta',
    JSON.stringify({
      customerId: options.forCustomer?.customerId,
      folderId,
      wrappedDekCustomer: toBase64(wrappedDek),
      filenameEncrypted: toBase64(encryptedFilename),
      contentTypeHint: aufbereitet.inhaltstyp || null,
      categoryLabel,
      embeddingModel,
      aiProcessed: Boolean(categoryLabel),
      sichtbarkeit,
      weitereWraps: weitereWraps.length ? weitereWraps : undefined,
    }),
  );
  form.append('document', new Blob([encryptedDocument as BlobPart]), 'doc.enc');

  if (extractedText.trim()) {
    // Text-Sidecar mit demselben DEK – genauso geschützt wie die Datei (3.10).
    const encryptedText = await encrypt(dek, utf8.encode(extractedText));
    form.append('text', new Blob([encryptedText as BlobPart]), 'text.enc');
  }
  if (structuredFields) {
    const encryptedFields = await encryptJson(dek, structuredFields);
    form.append('fields', new Blob([encryptedFields as BlobPart]), 'fields.enc');
  }
  if (embeddings?.length) {
    // Binär, nicht JSON – siehe encryptVectors in crypto/primitives.ts.
    const encryptedEmbedding = await encryptVectors(dek, embeddings);
    form.append('embedding', new Blob([encryptedEmbedding as BlobPart]), 'embedding.enc');
  }

  report('Wird hochgeladen');
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/documents`, {
    method: 'POST',
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(error.error ?? `Upload fehlgeschlagen (${response.status})`);
  }

  const ergebnis = (await response.json()) as { id: string };
  // Ordner für einen Dienst (Vertragsverwalter) freigegeben? Dann mitverpacken.
  if (!options.forCustomer) {
    const { nachAblage } = await import('./dienste.ts');
    await nachAblage(ergebnis.id, folderId, dek);
  }
  return ergebnis;
}

/**
 * Sucht einen passenden Ordner oder legt ihn an – beim Kunden für sich
 * selbst, bei einer Blindablage (`forCustomer` gesetzt) für genau den einen
 * zugeordneten Kunden. Ohne `forCustomer` lehnt der Server einen Buddy-Aufruf
 * ab, damit niemand aus Versehen in einem fremden Kundenordner landet.
 */
async function ensureFolder(pfad: string, forCustomer?: string, safeKundeId?: string): Promise<string | null> {
  const anfrage = forCustomer ? `?forCustomer=${forCustomer}` : safeKundeId ? `?kundeId=${safeKundeId}` : '';
  const { folders } = await api.get<{
    folders: Array<{ id: string; name: string; parent_id: string | null }>;
  }>(`/api/folders${anfrage}`);

  // Der Vorschlag der KI ist ein Pfad ("Rechnungen/Deutsche Post"), und daraus
  // wird hier eine echte Ordnerhierarchie. Vorher wurde der ganze Pfad als EIN
  // Ordnername angelegt — deshalb lagen bei einem echten Bestand 60 Ordner
  // flach nebeneinander, viele davon mit Schrägstrich im Namen (17.09.2026).
  const ebenen = pfad
    .split('/')
    .map((teil) => teil.trim())
    .filter((teil) => teil.length > 0)
    // Drei Ebenen: Oberordner / Firma / Art der Unterlage (17.10.2026).
    .slice(0, 3);
  if (ebenen.length === 0) return null;

  let parentId: string | null = null;
  // Explizit typisiert: Die Liste wächst unten um selbst angelegte Ordner,
  // TypeScript kann den Typ sonst nicht mehr ableiten.
  let bekannt: Array<{ id: string; name: string; parent_id: string | null }> = folders;

  for (const ebene of ebenen) {
    // Verglichen wird nur innerhalb derselben Ebene: „Sonstiges" unter
    // „Rechnungen" und „Sonstiges" ganz oben sind zwei verschiedene Ablagen.
    const geschwister = bekannt.filter((folder) => folder.parent_id === parentId);
    const treffer = findeAehnlichen(ebene, geschwister);

    if (treffer) {
      parentId = treffer.id;
      continue;
    }

    try {
      // Zwischenvariable, damit TypeScript die Kette `parentId` → Anfrage →
      // `created.id` → `parentId` nicht als zirkulär ansieht.
      const eltern: string | null = parentId;
      const created: { id: string } = await api.post<{ id: string }>('/api/folders', {
        name: ebene,
        parentId: eltern,
        ...(forCustomer ? { forCustomer } : {}),
        ...(safeKundeId && !forCustomer ? { kundeId: safeKundeId } : {}),
      });
      bekannt = [...bekannt, { id: created.id, name: ebene, parent_id: eltern }];
      parentId = created.id;
    } catch {
      // Nicht anlegbar (etwa Wettlauf mit einem zweiten Upload): Lieber die
      // schon erreichte Ebene nehmen als gar keinen Ordner.
      return parentId;
    }
  }

  return parentId;
}

/**
 * Ordner für den Sortier-Prompt (siehe `uploadDocument`) – als vollständige Pfade.
 * Bloße Namen führten dazu, dass die KI einen Unterordner („Helmut's Palast") als
 * ganzen Pfad zurückgab und er als neuer Oberordner entstand (17.10.2026).
 */
async function ordnerNamen(forCustomer?: string, safeKundeId?: string): Promise<string[]> {
  const anfrage = forCustomer ? `?forCustomer=${forCustomer}` : safeKundeId ? `?kundeId=${safeKundeId}` : '';
  const { folders } = await api.get<{ folders: Array<{ id: string; name: string; parent_id: string | null }> }>(
    `/api/folders${anfrage}`,
  );
  const nachId = new Map(folders.map((f) => [f.id, f]));
  const pfad = (f: { id: string; name: string; parent_id: string | null }, tiefe = 0): string => {
    const eltern = f.parent_id ? nachId.get(f.parent_id) : undefined;
    return eltern && tiefe < 5 ? `${pfad(eltern, tiefe + 1)}/${f.name}` : f.name;
  };
  return folders.map((f) => pfad(f)).sort((a, b) => a.localeCompare(b, 'de'));
}

/**
 * Vergleichsform eines Ordnernamens.
 *
 * Wirft alles weg, was zwei Namen trennt, ohne dass sie Verschiedenes
 * meinen: Groß-/Kleinschreibung, Umlaute, Bindewörter („und", „&"),
 * Zeichensetzung und der Plural-Unterschied („Versicherung" /
 * „Versicherungen"). „Zeugnisse & Bildung" und „Bildung und Zeugnisse"
 * werden damit zur selben Form.
 */
function vergleichsform(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss')
    .split(/[^a-z0-9]+/)
    .filter((wort) => wort.length > 0 && wort !== 'und' && wort !== 'der' && wort !== 'die' && wort !== 'das')
    // Ein angehängtes "n"/"en" unterscheidet nur die Zahl, nicht die Sache.
    .map((wort) => wort.replace(/(en|n)$/, ''))
    .sort()
    .join(' ');
}

/**
 * Namen, die für sich nichts bedeuten.
 *
 * „Sonstiges" unter „Rechnungen" und „Sonstiges" unter „Gesundheit" sind zwei
 * verschiedene Restestapel, keine Dublette. Bei solchen Namen bleibt es
 * deshalb bei der strengen Prüfung auf Gleichheit. Dasselbe gilt für reine
 * Jahreszahlen („2026").
 */
const SAMMELSTAEMME = ['sonstig', 'allgemei', 'divers', 'unsortiert', 'nicht sortiert', 'ubrig'];

function istSammelname(form: string): boolean {
  // Auf den Stamm geprüft, nicht auf Gleichheit: Die Vergleichsform kürzt nur
  // "en"/"n" weg, "Sonstiges" bleibt also "sonstiges" und "Sonstige" wird
  // "sonstige" — beide sollen greifen.
  return SAMMELSTAEMME.some((stamm) => form.startsWith(stamm)) || /^[0-9]+$/.test(form);
}

/**
 * Sucht unter gleichrangigen Ordnern einen, der dasselbe meint wie `name`.
 *
 * Vorher wurde nur auf Gleichheit geprüft. Das ließ Dubletten entstehen,
 * sobald die KI einen Namen geringfügig anders formulierte — bei einem echten
 * Bestand zehn Ordner rund um Arbeitszeugnisse (17.09.2026).
 *
 * Der Aufrufer übergibt ausschließlich Ordner derselben Ebene: Ein Treffer
 * quer durch den Baum wäre falsch, weil derselbe Name unter verschiedenen
 * Oberordnern Verschiedenes meint.
 */
function findeAehnlichen<T extends { id: string; name: string }>(
  name: string,
  geschwister: T[],
): T | null {
  const gesucht = vergleichsform(name);
  if (!gesucht) return null;

  const genau = geschwister.find((folder) => vergleichsform(folder.name) === gesucht);
  if (genau) return genau;

  if (istSammelname(gesucht)) return null;

  // Ein Name, der den anderen vollständig enthält, meint dieselbe Sache:
  // „Rohrreinigung" und „Rohrreinigung Binfet". Der kürzere gewinnt, er ist
  // der allgemeinere Sammelpunkt.
  const enthalten = geschwister
    .filter((folder) => {
      const form = vergleichsform(folder.name);
      if (!form || istSammelname(form)) return false;
      return form.startsWith(gesucht) || gesucht.startsWith(form);
    })
    .sort((a, b) => a.name.length - b.name.length);

  return enthalten[0] ?? null;
}

// ---------------------------------------------------------------------------
// Lesen
// ---------------------------------------------------------------------------

/**
 * Ermittelt, mit welchem bereits entsperrten Schlüssel sich `meta.wrapped_dek`
 * öffnen lässt (Migration 007/010, Schlüsselkreise) — der klassische Weg über
 * den eigenen Kontoschlüssel bleibt der Normalfall, solange kein
 * `wrap_ziel_art` mitkommt oder es `'customer'` ist.
 */
async function schluesselFuerDokument(
  meta: DocumentMeta,
): Promise<{ algorithm: KeyAlgorithm; privateKey: CryptoKey }> {
  if (!meta.wrap_ziel_art || meta.wrap_ziel_art === 'customer') {
    const vault = requireVault();
    return { algorithm: vault.algorithm, privateKey: vault.privateKey };
  }
  if (meta.wrap_ziel_art === 'person') {
    // Gast-Umschlag (Familien-Safes): für meinen eigenen Personenschlüssel verpackt –
    // derselbe wie der Kontoschlüssel seit dem Schlüssel-Upgrade.
    const vault = requireVault();
    return { algorithm: vault.algorithm, privateKey: vault.privateKey };
  }
  if (meta.wrap_ziel_art === 'organisation') {
    // Irgendein Safe der Sitzung (alle sind aufgeschlossen) – zur Sicherheit noch der
    // alte Weg über die gewählte Organisation.
    const safe = meta.wrap_ziel_id ? safeSchluessel(meta.wrap_ziel_id) : null;
    const organisation = safe ?? currentOrganisation();
    if (!organisation) throw new Error('Dieser Safe ist nicht aufgeschlossen');
    return { algorithm: organisation.algorithm, privateKey: organisation.privateKey };
  }
  // 'kreis'
  const gefunden = meta.wrap_ziel_id ? await kreisSchluessel(meta.wrap_ziel_id) : null;
  if (!gefunden) throw new Error('Der Kreis, dem dieses Dokument gehört, ist nicht entsperrt');
  return gefunden;
}

/** Entschlüsselt Dateiname und DEK – ohne die Datei selbst zu laden. */
export async function openDocument(meta: DocumentMeta): Promise<OpenedDocument> {
  const { algorithm, privateKey } = await schluesselFuerDokument(meta);
  const dek = await unwrapDek(fromBase64(meta.wrapped_dek), privateKey, algorithm);
  const filename = utf8.decode(await decrypt(dek, fromBase64(meta.filename_encrypted)));
  return { id: meta.id, filename, dek, meta };
}

export async function downloadDocument(document: OpenedDocument): Promise<Blob> {
  const encrypted = await api.getBytes(`/api/documents/${document.id}/content`);
  const plaintext = await decrypt(document.dek, encrypted);
  return new Blob([plaintext as BlobPart], {
    type: document.meta.content_type_hint ?? 'application/octet-stream',
  });
}

export async function loadText(document: OpenedDocument): Promise<string | null> {
  if (!document.meta.has_text) return null;
  const encrypted = await api.getBytes(`/api/documents/${document.id}/sidecar/text`);
  return utf8.decode(await decrypt(document.dek, encrypted));
}

export async function loadFields(
  document: OpenedDocument,
): Promise<StructuredFields | null> {
  if (!document.meta.has_fields) return null;
  const encrypted = await api.getBytes(`/api/documents/${document.id}/sidecar/fields`);
  return decryptJson<StructuredFields>(document.dek, encrypted);
}

export async function loadEmbeddings(
  document: OpenedDocument,
): Promise<Float32Array[] | null> {
  if (!document.meta.has_embedding) return null;
  const encrypted = await api.getBytes(`/api/documents/${document.id}/sidecar/embedding`);
  return decryptVectors(document.dek, encrypted);
}

/** `alleSafes`: Kundenansicht über alle Safes (Familien-Safes); Buddy und Altweg bleiben bei /api/documents. */
export async function listDocuments(alleSafes = false): Promise<DocumentMeta[]> {
  const { documents } = await api.get<{ documents: DocumentMeta[] }>(alleSafes ? '/api/safes/dokumente' : '/api/documents');
  return documents;
}

export interface AltbestandErgebnis {
  id: string;
  filename: string;
  ok: boolean;
  fehler?: string;
}

/**
 * Übernimmt alle noch klassisch (am eigenen Kontoschlüssel) gewrappten
 * Bestandsdokumente in die aktuell gewählte (private) Organisation —
 * `wrap_ziel_art` fehlt oder ist `'customer'` nur noch bei Dokumenten von vor
 * dem Schlüssel-Upgrade (Migration 007/010); alles andere ist bereits
 * migriert oder gehört zu einem Kreis.
 *
 * Läuft Dokument für Dokument über den bestehenden Einzel-Verschieben-Weg
 * (`dokumentInOrganisationVerschieben`) statt über einen Batch-Endpunkt: Ein
 * Fehlschlag mittendrin darf die übrigen nicht verhindern, und ein erneuter
 * Aufruf nach einem Teilfehlschlag holt automatisch nur noch die noch nicht
 * migrierten nach, weil bereits verschobene Dokumente aus dem Filter fallen.
 */
export async function alteDokumenteUebernehmen(
  onFortschritt?: (erledigt: number, gesamt: number) => void,
  safe?: { publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm },
): Promise<AltbestandErgebnis[]> {
  const alle = await listDocuments();
  const altbestand = alle.filter((meta) => !meta.wrap_ziel_art || meta.wrap_ziel_art === 'customer');

  const ergebnisse: AltbestandErgebnis[] = [];
  for (const meta of altbestand) {
    let filename = meta.id;
    try {
      const geoeffnet = await openDocument(meta);
      filename = geoeffnet.filename;
      await dokumentInOrganisationVerschieben(meta.id, geoeffnet.dek, safe);
      ergebnisse.push({ id: meta.id, filename, ok: true });
    } catch (error) {
      ergebnisse.push({
        id: meta.id,
        filename,
        ok: false,
        fehler: error instanceof Error ? error.message : 'Unbekannter Fehler',
      });
    }
    onFortschritt?.(ergebnisse.length, altbestand.length);
  }
  return ergebnisse;
}

/**
 * Freigabe erteilen (Konzept 6.2). Die DEKs werden lokal entschlüsselt und
 * für den Buddy neu gewrappt – der Server bekommt nur das Ergebnis.
 */
export async function grantAccess(options: {
  documents: DocumentMeta[];
  scope: 'documents' | 'folder' | 'all';
  scopeFolderId?: string | null;
  expiresAt: Date;
  reason?: string;
  /** Ticketbezug aus der Einladung, siehe ticketBezug() in main.ts. */
  ticketReference?: string;
  ticketSubject?: string;
}): Promise<string> {
  const buddy = await api.get<{
    buddyAccountId: string;
    algorithm: KeyAlgorithm;
    publicKey: string;
  }>('/api/keys/my-buddy/public');

  const buddyPublicRaw = fromBase64(buddy.publicKey);
  await importPublicKey(buddyPublicRaw, buddy.algorithm);   // frühes Fehlschlagen bei defektem Schlüssel

  const wrappedDeks = [];
  for (const meta of options.documents) {
    // Ein Dokument kann inzwischen über die Organisation oder einen Kreis
    // gewrappt sein statt klassisch am eigenen Kontoschlüssel (Migration
    // 007/010) — derselbe Auswahlweg wie beim Öffnen.
    const { algorithm, privateKey } = await schluesselFuerDokument(meta);
    const dek = await unwrapDek(fromBase64(meta.wrapped_dek), privateKey, algorithm);
    wrappedDeks.push({
      documentId: meta.id,
      wrappedDekBuddy: toBase64(await wrapDekFor(dek, buddyPublicRaw, buddy.algorithm)),
    });
  }

  const result = await api.post<{ grantGroupId: string }>('/api/grants', {
    scope: options.scope,
    scopeFolderId: options.scopeFolderId ?? null,
    reason: options.reason ?? null,
    ticketReference: options.ticketReference ?? null,
    ticketSubject: options.ticketSubject ?? null,
    expiresAt: options.expiresAt.toISOString(),
    wrappedDeks,
  });
  return result.grantGroupId;
}
